import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { usePartnersInviteMember } from '@/api/hooks';
import { type PartnerMemberInviteResponse } from '@/api/routes/partners/partners.members.api';
import Button from '@/components/Button';
import FormGlobalError from '@/components/forms/FormGlobalError';
import { IconCopy } from '@/components/icon/tabler/IconCopy';
import Modal from '@/components/Modal';
import { inviteMemberSchema } from '@/domain/account/schemas';
import { type InviteMemberFormValues } from '@/domain/account/types';
import {
  formatAccountDate,
  PARTNER_INVITABLE_ROLES,
  toMemberInviteRequest,
} from '@/domain/account/utils';
import { useApiFormErrors, useServerErrorsStore } from '@/hooks/useApiFormErrors';
import { useTranslate } from '@/state/i18n';
import { copyText } from '@/utils/clipboard';
import { withServerErrors } from '@/utils/errors';

interface Props {
  open: boolean;
  onClose: () => void;
}

const INVITE_FIELDS = new Set(['email', 'role']);

/** Invite by e-mail as editor or viewer. After sending, the link can also be copied and shared. */
const InviteMemberModal = ({ open, onClose }: Props) => {
  const t = useTranslate('domain.account.components.InviteMemberModal');
  const { enqueueSnackbar } = useSnackbar();
  const [sent, setSent] = useState<PartnerMemberInviteResponse | null>(null);
  const { mutateAsync: invite, isPending } = usePartnersInviteMember();

  const serverErrors = useServerErrorsStore();
  const resolver = useMemo(
    () => withServerErrors(joiResolver(inviteMemberSchema), serverErrors),
    [serverErrors],
  );
  const { control, handleSubmit, setError, getValues, reset } = useForm<InviteMemberFormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues: { email: '', role: 'editor' },
  });
  const { applyApiError, globalError, clearGlobalError, clearApiErrors } =
    useApiFormErrors<InviteMemberFormValues>({
      setError,
      getValues,
      serverErrors,
      isVisibleField: (path) => INVITE_FIELDS.has(path),
      fallbackMessage: t('submitError'),
    });

  // Starts clean on every opening; resetting on close would flash the form during the exit.
  useEffect(() => {
    if (!open) return;
    setSent(null);
    clearApiErrors();
    reset();
  }, [open, clearApiErrors, reset]);

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const onSubmit = async (values: InviteMemberFormValues) => {
    clearApiErrors();
    try {
      setSent(await invite(toMemberInviteRequest(values)));
      enqueueSnackbar(t('sent'), { variant: 'success' });
    } catch (error) {
      applyApiError(error);
    }
  };

  const handleCopy = async () => {
    if (!sent) return;
    const copied = await copyText(sent.inviteUrl);
    enqueueSnackbar(t(copied ? 'copied' : 'copyError'), { variant: copied ? 'success' : 'error' });
  };

  return (
    <Modal
      open={open}
      title={t(sent ? 'sentTitle' : 'title')}
      onDismiss={handleClose}
      closeDisabled={isPending}
      actions={
        sent ? (
          <Button variant="contained" onClick={handleClose}>
            {t('done')}
          </Button>
        ) : (
          <>
            <Button variant="text" onClick={handleClose} disabled={isPending}>
              {t('cancel')}
            </Button>
            <Button variant="contained" loading={isPending} onClick={handleSubmit(onSubmit)}>
              {t('submit')}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t('sentText', {
              email: sent.invite.email,
              date: formatAccountDate(sent.invite.expiresAt),
            })}
          </Typography>
          <TextField
            value={sent.inviteUrl}
            fullWidth
            size="small"
            slotProps={{ htmlInput: { readOnly: true, 'aria-label': t('link') } }}
          />
          <Button variant="outlined" startIcon={<IconCopy />} onClick={handleCopy}>
            {t('copy')}
          </Button>
        </Stack>
      ) : (
        <Stack component="form" noValidate gap={2.5} onSubmit={handleSubmit(onSubmit)}>
          <FormGlobalError message={globalError} onClose={clearGlobalError} />
          <Typography variant="body2" color="text.secondary">
            {t('description')}
          </Typography>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth>
                <FormLabel htmlFor="invite-email">{t('email')}</FormLabel>
                <TextField
                  {...field}
                  id="invite-email"
                  type="email"
                  autoComplete="off"
                  autoFocus
                  placeholder={t('emailPlaceholder')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              </FormControl>
            )}
          />
          <Controller
            name="role"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={Boolean(fieldState.error)}>
                <FormLabel id="invite-role">{t('role')}</FormLabel>
                <RadioGroup {...field} aria-labelledby="invite-role">
                  {PARTNER_INVITABLE_ROLES.map((role) => (
                    <FormControlLabel
                      key={role}
                      value={role}
                      control={<Radio />}
                      label={
                        <Stack gap={0.25} py={0.75}>
                          <Typography variant="body2" fontWeight={600}>
                            {t(`roles.${role}.label`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(`roles.${role}.description`)}
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>
                {fieldState.error ? (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                ) : null}
              </FormControl>
            )}
          />
        </Stack>
      )}
    </Modal>
  );
};

export default InviteMemberModal;
